import { useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import DataObjectIcon from "@mui/icons-material/DataObject";
import {
  BUILTIN_PREFIX,
  TEMPLATE_BUILTINS,
  checkTemplate,
} from "../../logic/action-set-template";
import { FIELD_LABELS } from "./constants";

export type TemplateTextFieldProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  /** Shown when the template is sound; a problem replaces it. */
  helperText?: string;
  /**
   * The enclosing action set's input names, or `null` in a rule's own action —
   * rules have no inputs, so only `{$field}` is offered there.
   */
  inputNames: readonly string[] | null;
  autoFocus?: boolean;
};

/**
 * A text field whose value is template source: `{$merchant}` for a field of
 * the transaction being edited, `{Navn}` for an input of the action set this
 * action belongs to.
 *
 * The insert menu is the discoverability story — nothing about a plain text box
 * suggests braces mean anything — and the helper line names a placeholder that
 * resolves to nothing before the save round-trip does.
 */
export const TemplateTextField = ({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  inputNames,
  autoFocus,
}: TemplateTextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const problems = checkTemplate(value, inputNames);

  const insert = (placeholder: string) => {
    setAnchor(null);
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + placeholder + value.slice(end));
    // Put the caret after what was just inserted, once React has re-rendered.
    requestAnimationFrame(() => {
      const caret = start + placeholder.length;
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  const problemText = problems
    .map((p) =>
      p.type === "malformed"
        ? "Unbalanced { } — write {{ for a literal brace"
        : p.name.startsWith(BUILTIN_PREFIX)
          ? `There is no field called ${p.name.slice(1)}`
          : `No input named ${p.name}${inputNames ? "" : " — did you mean {$" + p.name + "}?"}`,
    )
    .join(". ");

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputRef={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={problems.length > 0}
        helperText={problemText || helperText}
      />
      <Box>
        <Button
          size="small"
          startIcon={<DataObjectIcon />}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ whiteSpace: "nowrap", mt: 0.25 }}
        >
          Insert
        </Button>
        <Menu
          anchorEl={anchor}
          open={anchor !== null}
          onClose={() => setAnchor(null)}
        >
          {inputNames && inputNames.length > 0 && (
            <ListSubheader>Inputs of this action set</ListSubheader>
          )}
          {(inputNames ?? []).map((name) => (
            <MenuItem key={name} onClick={() => insert(`{${name}}`)}>
              {`{${name}}`}
            </MenuItem>
          ))}
          {inputNames && inputNames.length > 0 && <Divider />}
          <ListSubheader>From the transaction</ListSubheader>
          {TEMPLATE_BUILTINS.map((field) => (
            <MenuItem
              key={field}
              onClick={() => insert(`{${BUILTIN_PREFIX}${field}}`)}
            >
              {FIELD_LABELS[field]}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Stack>
  );
};
